import axios from "axios";
import api from "config/axiosConfig";
import { clearCsrfToken, getCsrfToken, setCsrfToken } from "util/csrf";
import { apiRoutes } from "util/apiRoutes";

/**
 * Listener invoked whenever the in-memory access token mutates (set,
 * refreshed, or cleared). Receives the new token value, or `null` when
 * the token has been cleared. Used by long-lived consumers such as the
 * Socket.IO provider to swap their auth payload without remounting.
 */
export type AuthTokenListener = (token: string | null) => void;

class AuthService {
  // In-memory storage for the access token
  private static accessToken: string | null = null;
  private static csrfPromise: Promise<void> | null = null;
  // Prevent multiple concurrent refresh requests
  private static refreshPromise: Promise<void> | null = null;
  private static lastRefreshTime: number = 0;
  private static REFRESH_COOLDOWN = 10000; // 10 seconds cooldown between refreshes
  // Subscribers notified when the access token changes (set / cleared).
  // Kept on the class so any module can react without a global event bus.
  private static tokenListeners: Set<AuthTokenListener> = new Set();

  /**
   * Subscribe to access-token changes. Returns an unsubscribe function so
   * callers can clean up in `useEffect` teardown.
   */
  static onTokenChange(listener: AuthTokenListener): () => void {
    this.tokenListeners.add(listener);
    return () => {
      this.tokenListeners.delete(listener);
    };
  }

  /**
   * Notify all current subscribers about the latest token value.
   * Errors thrown by listeners must not prevent subsequent listeners
   * from being called.
   */
  private static notifyTokenListeners(): void {
    const snapshot = Array.from(this.tokenListeners);
    for (const listener of snapshot) {
      try {
        listener(this.accessToken);
      } catch {
        // Swallow listener errors so one bad subscriber does not break
        // the auth flow for everyone else.
      }
    }
  }

  /**
   * Set the access token in memory.
   */
  static setAccessToken(token: string): void {
    const previous = this.accessToken;
    this.accessToken = token;
    if (previous !== token) {
      this.notifyTokenListeners();
    }
  }

  /**
   * Get the current access token.
   */
  static getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Clear the access token.
   */
  static clearAccessToken(): void {
    const previous = this.accessToken;
    this.accessToken = null;
    this.lastRefreshTime = 0;
    if (previous !== null) {
      this.notifyTokenListeners();
    }
  }

  /**
   * Ensure the browser has a valid CSRF token cookie before mutating requests.
   *
   * Pass `force` to refetch even when a token is already present. The 403/CSRF
   * response interceptor uses this to recover from a *rejected* token — a stale
   * cross-environment `.societea.app` cookie, or a token signed with a rotated
   * CSRF secret. Without `force`, `getCsrfToken()` keeps returning the bad
   * cookie value, so the early return below suppresses the refresh and the
   * retry resends the same rejected token.
   */
  static async ensureCsrfToken(force = false): Promise<void> {
    if (!force && getCsrfToken()) {
      return;
    }

    if (this.csrfPromise) {
      return this.csrfPromise;
    }

    this.csrfPromise = axios
      .get(`${process.env.REACT_APP_API_URL || ""}${apiRoutes.auth.csrf}`, {
        withCredentials: true,
      })
      .then((response) => {
        setCsrfToken(response.data?.csrfToken);
      })
      .finally(() => {
        this.csrfPromise = null;
      });

    return this.csrfPromise;
  }

  /**
   * Sign in using local authentication.
   * Expects the caller to supply the email and password.
   */
  static async login(email: string, password: string): Promise<void> {
    try {
      const response = await api.post(apiRoutes.auth.signin, { email, password });
      if (response.status === 200 && response.data.accessToken) {
        this.setAccessToken(response.data.accessToken);
      } else {
        throw new Error('Sign in failed: unexpected response');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Register a new local user.
   * Expects the caller to supply the email, password, username, and PKCE parameters.
   */
  static async register(
    email: string,
    password: string,
    username: string,
    codeVerifier: string,
    codeChallenge: string
  ): Promise<void> {
    try {
      const response = await api.post(apiRoutes.auth.register, {
        email,
        password,
        username,
        code_verifier: codeVerifier,
        code_challenge: codeChallenge,
      });
      if (response.status === 201 && response.data.accessToken) {
        this.setAccessToken(response.data.accessToken);
      } else {
        throw new Error('Registration failed: unexpected response.');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Refresh the access token.
   * Uses a static refreshPromise to avoid multiple simultaneous refresh calls.
   */
  static async refresh(force = false): Promise<void> {
    // Check if we've refreshed recently
    const now = Date.now();
    if (!force && this.accessToken && now - this.lastRefreshTime < this.REFRESH_COOLDOWN) {
      return;
    }

    // If we're already refreshing, wait for that to complete
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    try {
      this.refreshPromise = new Promise(async (resolve, reject) => {
        try {
          const response = await api.post(apiRoutes.auth.refresh);
          if (response.status === 200 && response.data.accessToken) {
            this.setAccessToken(response.data.accessToken);
            this.lastRefreshTime = Date.now();
            resolve();
          } else {
            this.clearAccessToken();
            reject(new Error('Token refresh failed: unexpected response'));
          }
        } catch (error) {
          this.clearAccessToken();
          reject(error);
        }
      });

      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Log out the current user.
   * Clears the local access token and calls the backend logout endpoint.
   */
  static async logout(): Promise<void> {
    try {
      await this.ensureCsrfToken();
      await api.post(apiRoutes.auth.signout);
    } finally {
      this.clearAccessToken();
      clearCsrfToken();
    }
  }
}

export default AuthService;